import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { knowledgeBaseService, KnowledgeArticle } from '../services/knowledgeBaseService';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Loading } from '../components/Loading';
import { Badge } from '../components/Badge';
import { ArticleRating } from '../components/ArticleRating';
import { RichTextRenderer } from '../components/editor/RichTextRenderer';
import { formatSydneyTime } from '@/utils/timezone';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface KnowledgeArticleViewProps {}

export const KnowledgeArticleView: React.FC<KnowledgeArticleViewProps> = () => {
  useDocumentTitle('Knowledge Article | SupportDesk');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState('');
  const [usageSaved, setUsageSaved] = useState(false);
  const [usageSaving, setUsageSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadArticle(id);
    }
  }, [id]);

  const loadArticle = async (articleId: string) => {
    try {
      setLoading(true);
      setError(null);
      setActionMessage('');
      setUsageSaved(false);
      const articleData = await knowledgeBaseService.getArticle(articleId, true);
      // BugHerd #167: /api/v1/knowledge/articles/1 used to fall through to the SPA fallback
      // and return index.html with HTTP 200, so this path was reached with an HTML document
      // instead of an article and the page blanked further down. The API now 404s such
      // paths; this guard means a non-article 200 can never blank the page again.
      if (!articleData || typeof articleData !== 'object' || !(articleData as { id?: unknown }).id) {
        setError('Article not found');
        setArticle(null);
        return;
      }
      setArticle(articleData);
    } catch (err: any) {
      console.error('Error loading article:', err);
      if (err.response?.status === 404) {
        setError('Article not found');
      } else {
        setError('Failed to load article. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getArticleTypeLabel = (type: number): string => {
    const types = ['General', 'How To', 'Troubleshooting', 'FAQ', 'Policy', 'Training', 'Best Practice'];
    return types[type] || 'General';
  };

  const getArticleTypeColor = (type: number): string => {
    const colors = [
      'bg-gray-100 text-gray-800',
      'bg-blue-100 text-blue-800',
      'bg-red-100 text-red-800',
      'bg-purple-100 text-purple-800',
      'bg-yellow-100 text-yellow-800',
      'bg-green-100 text-green-800',
      'bg-indigo-100 text-indigo-800'
    ];
    return colors[type] || colors[0];
  };

  const getStatusColor = (status: number): string => {
    const colors = [
      'bg-gray-100 text-gray-800',
      'bg-yellow-100 text-yellow-800',
      'bg-green-100 text-green-800',
      'bg-red-100 text-red-800',
      'bg-purple-100 text-purple-800'
    ];
    return colors[status] || colors[0];
  };

  const getStatusLabel = (status: number): string => {
    const statuses = ['Draft', 'Review', 'Published', 'Archived', 'Deprecated'];
    return statuses[status] || 'Draft';
  };

  const formatDate = (dateString: string) => formatSydneyTime(dateString);

  const handleUsageIncrement = async () => {
    if (!article || usageSaved || usageSaving) return;
    setUsageSaving(true);
    try {
      await knowledgeBaseService.incrementArticleUsage(article.id);
      setUsageSaved(true);
      setActionMessage('Thanks for your feedback.');
    } catch {
      setActionMessage('Unable to save your feedback. Please try again.');
    } finally {
      setUsageSaving(false);
    }
  };

  const handleShare = async () => {
    if (!article) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setActionMessage('Article link copied.');
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setActionMessage('Unable to share automatically. Copy the address from your browser.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  if (error || !article) {
    // Deliberately not a <main>: MainLayout already provides the page landmark, so nesting a
    // second one would add a duplicate landmark rather than fix anything.
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="article-error-state">
        <Card>
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{error || 'Article not found'}</h3>
            <p className="text-gray-500 mb-4">
              The article you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/knowledge-base')} variant="primary">
              Back to Knowledge Base
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Link to="/knowledge-base" className="hover:text-blue-600">
            Knowledge Base
          </Link>
          <span>/</span>
          {article.category && (
            <>
              <span className="text-gray-600">
                {article.category.name}
              </span>
              <span>/</span>
            </>
          )}
          <span className="text-gray-900 font-medium truncate">{article.title}</span>
        </div>
      </nav>

      <Card>
        <div className="p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Badge className={getArticleTypeColor(article.type)}>
                  {getArticleTypeLabel(article.type)}
                </Badge>
                <Badge className={getStatusColor(article.status)}>
                  {getStatusLabel(article.status)}
                </Badge>
                {article.isFeatured && (
                  <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>
                )}
              </div>
              
              {user && (user.role === 1 || user.role === 2 || user.role === 3) && (
                <Link to={`/knowledge-base/admin?edit=${article.id}`}>
                  <Button variant="outline" size="sm">
                    Edit Article
                  </Button>
                </Link>
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{article.title}</h1>
            
            {article.summary && (
              <p className="text-lg text-gray-600 mb-4">{article.summary}</p>
            )}
            
            <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
              <div className="flex items-center space-x-6">
                <span>By {article.author?.fullName || 'Unknown Author'}</span>
                <span>{article.viewCount} views</span>
                <span>Published {formatDate(article.publishedAt || article.createdAt)}</span>
                {article.updatedAt && article.updatedAt !== article.createdAt && (
                  <span>Updated {formatDate(article.updatedAt)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none mb-8">
            <RichTextRenderer content={article.content} htmlContent={article.htmlContent} />
          </div>

          {/* Tags */}
          {article.tags && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Tags:</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.split(',').map((tag, index) => (
                  <Badge key={index} className="bg-gray-100 text-gray-700">
                    #{tag.trim()}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {article.attachments && article.attachments.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Attachments:</h3>
              <div className="space-y-2">
                {article.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-900">{attachment.displayName}</span>
                    <span className="text-xs text-gray-500">
                      ({(attachment.fileSize / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collaborators */}
          {article.collaborators && article.collaborators.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Contributors:</h3>
              <div className="flex flex-wrap gap-2">
                {article.collaborators.map((collaborator) => (
                  <div key={collaborator.id} className="flex items-center space-x-1 text-sm text-gray-600">
                    <span>{collaborator.user?.fullName}</span>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      {collaborator.role === 0 ? 'Contributor' : 
                       collaborator.role === 1 ? 'Editor' :
                       collaborator.role === 2 ? 'Reviewer' : 'Owner'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleUsageIncrement}
                  disabled={usageSaved || usageSaving}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {usageSaved ? 'Marked helpful' : 'Helpful'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleShare}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share
                </Button>
              </div>
            </div>
          </div>

          {actionMessage && <p role="status" className="mb-4 text-sm text-gray-700">{actionMessage}</p>}

          {/* Rating Component */}
          <ArticleRating 
            articleId={article.id}
            currentRating={article.rating}
            ratingCount={article.ratingCount}
          />
        </div>
      </Card>

      {/* Back to Knowledge Base */}
      <div className="mt-8 text-center">
        <Button onClick={() => navigate('/knowledge-base')} variant="outline">
          ← Back to Knowledge Base
        </Button>
      </div>
    </div>
  );
};

export default KnowledgeArticleView;
